import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Avatar,
  Grid,
  InputAdornment,
  Paper,
  Fade,
  alpha,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { supabase } from '../supabaseClient';

const ProfileSetupContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: '#ffffff'
}));

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  width: '100%',
  borderRadius: 24,
  background: 'white',
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
  position: 'relative',
  overflow: 'hidden',
  transformOrigin: 'center',
  backfaceVisibility: 'hidden',
  perspective: 1000,
  willChange: 'transform, box-shadow',
  WebkitTransition: 'all 0.6s ease-out !important',
  MozTransition: 'all 0.6s ease-out !important',
  transition: 'all 0.6s ease-out !important',
  '&:hover': {
    transform: 'translateY(-8px) !important',
    boxShadow: `0 32px 64px ${alpha(theme.palette.primary.main, 0.3)} !important`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.5)} !important`,
    WebkitTransform: 'translateY(-8px) !important',
    MozTransform: 'translateY(-8px) !important'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
    opacity: 0.8,
    zIndex: 1
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 1)',
    fontSize: '1rem',
    minHeight: 56,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
    transition: 'all 0.3s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.6)',
    backgroundColor: 'rgba(255, 255, 255, 1)',
    padding: '0 8px',
    marginLeft: '-8px',
    transition: 'all 0.3s ease',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      backgroundColor: 'rgba(255, 255, 255, 1)'
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      padding: '0 8px'
    }
  },
  marginBottom: theme.spacing(3)
}));

const SaveButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2, 6),
  fontWeight: 500,
  fontSize: '1.1rem',
  textTransform: 'none',
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px) scale(1.02)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
  },
  '&:disabled': {
    background: alpha(theme.palette.grey[400], 0.5),
    color: alpha(theme.palette.grey[600], 0.8),
    boxShadow: 'none',
    transform: 'none'
  }
}));

const ProfileSetup = ({ onProfileComplete, currentUser }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (error) return;
      
      if (data) {
        setProfileData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          phone: data.phone || ''
        });
        // Don't auto-complete if they have name already - let them go through the flow
      }

      await loadAvatarFromStorage();
    } catch (err) {
      // Silent fail
    }
  };

  const loadAvatarFromStorage = async () => {
    try {
      const { data: files, error } = await supabase.storage
        .from('profile-avatars')
        .list(`${currentUser.id}/`, {
          limit: 1,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) return;

      if (files && files.length > 0) {
        const fileName = `${currentUser.id}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (signedUrlError) return;
        
        setAvatarUrl(data.signedUrl);
      } else {
        setAvatarUrl('');
      }
    } catch (err) {
      // Silent fail
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    
    if (error) setError(null);
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    setError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser.id}/avatar.${fileExt}`;

      const { data, error: uploadError } = await supabase.storage
        .from('profile-avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('profile-avatars')
        .createSignedUrl(fileName, 3600);

      if (signedUrlError) throw signedUrlError;

      setAvatarUrl(signedUrlData.signedUrl);

      setTimeout(() => {
        loadAvatarFromStorage();
      }, 500);

    } catch (err) {
      setError('Failed to upload avatar: ' + err.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData.first_name?.trim() || !profileData.last_name?.trim()) {
      setError('First name and last name are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', currentUser.id)
        .maybeSingle();
      
      if (!existingProfile) {
        // Create profile with all fields
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: currentUser.id,
            role: 'user',
            ...profileData
            // Don't set full_name - it's a generated column
          });
        
        if (insertError) throw insertError;
      } else {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            ...profileData
            // Don't set full_name - it's a generated column
          })
          .eq('id', currentUser.id);
        
        if (updateError) throw updateError;
      }

      setSuccess(true);
      
      setTimeout(() => {
        onProfileComplete?.();
      }, 1500);
    } catch (err) {
      setError('Failed to save profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    const first = profileData.first_name?.charAt(0) || '';
    const last = profileData.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || currentUser.email?.charAt(0).toUpperCase() || 'U';
  };

  if (success) {
    return (
      <ProfileSetupContainer>
        <Fade in timeout={800}>
          <StyledCard>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Box sx={{ mb: 4 }}>
                <Avatar sx={{ 
                  width: 80, 
                  height: 80, 
                  margin: '0 auto', 
                  bgcolor: theme.palette.success.main,
                  fontSize: '2rem',
                  fontWeight: 500
                }}>
                  ✓
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 500, mb: 2, color: theme.palette.success.main }}>
                Profile Complete!
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
                Welcome to GlowWorks, {profileData.first_name}!
              </Typography>
            </CardContent>
          </StyledCard>
        </Fade>
      </ProfileSetupContainer>
    );
  }

  return (
    <ProfileSetupContainer>
      <Fade in timeout={600}>
        <StyledCard>
          <CardContent sx={{ p: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                Complete Your Profile
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
                Let's get to know you better! Please provide your information.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar 
                  key={avatarUrl}
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    bgcolor: alpha(theme.palette.grey[400], 0.3),
                    fontSize: '2.5rem',
                    fontWeight: 500,
                    color: alpha(theme.palette.grey[600], 0.8),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`
                  }}
                  src={avatarUrl || undefined}
                  onLoad={() => setAvatarLoaded(true)}
                  onError={() => setAvatarLoaded(false)}
                >
                  {!avatarUrl && (
                    profileData.first_name || profileData.last_name ? 
                      getInitials() : 
                      <AccountCircleIcon sx={{ fontSize: 80, color: alpha(theme.palette.grey[600], 0.6) }} />
                  )}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                  type="file"
                  onChange={handleAvatarUpload}
                  disabled={uploadingAvatar}
                />
                <label htmlFor="avatar-upload">
                  <                  Button
                    component="span"
                    disabled={uploadingAvatar}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      minWidth: 0,
                      bgcolor: 'white',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                      '&:hover': { 
                        bgcolor: 'white',
                        transform: 'translateY(-1px) scale(1.05)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        border: `1px solid ${theme.palette.primary.main}`
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {uploadingAvatar ? (
                      <CircularProgress size={16} sx={{ color: alpha(theme.palette.grey[600], 0.8) }} />
                    ) : (
                      <PhotoCameraIcon sx={{ fontSize: 20, color: alpha(theme.palette.grey[600], 0.8) }} />
                    )}
                  </Button>
                </label>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={profileData.first_name}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={profileData.last_name}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="(123) 456-7890"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <SaveButton
                onClick={handleSaveProfile}
                disabled={loading || !profileData.first_name?.trim() || !profileData.last_name?.trim()}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {loading ? 'Saving Profile...' : 'Complete Profile'}
              </SaveButton>
            </Box>

            <Typography variant="caption" sx={{ 
              display: 'block',
              textAlign: 'center',
              mt: 3,
              color: 'text.secondary'
            }}>
              This information will be visible to your team members
            </Typography>
          </CardContent>
        </StyledCard>
      </Fade>
    </ProfileSetupContainer>
  );
};

export default ProfileSetup;